/**
 * Per-tenant rent schedule editor.
 *
 * Loads every month between the tenant's move-in and move-out and lets the
 * admin override the **due day**, **amount**, and **note** for any month,
 * or skip the month entirely so the tenant doesn't appear on the Rent
 * Tracking list for it. Overrides are persisted on the `rent_payments`
 * doc and only affect what the Rent Tracking page displays — the tenant
 * document and Rent Roll see the original `monthly_rent` / `rent_due_date`.
 *
 * Each row has explicit local draft state and a Save button — edits don't
 * commit until the user clicks Save (per the user's request). Clicking
 * Save fires the upsert and updates the in-memory cache so the rent
 * tracking page reflects the change as soon as the dialog is closed.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Loader2, RotateCcw, CalendarDays, Save, Ban, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  getTenantRentSchedule, updateTenantRentScheduleMonth, bulkUpdateTenantDueDay,
} from '@/lib/api';

const MONTH_NAMES = ['', 'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

const fmtMoney = (v) =>
  v == null || v === '' ? '-' : `$${parseFloat(v).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const isCurrentMonth = (y, m) => {
  const d = new Date();
  return d.getFullYear() === y && d.getMonth() + 1 === m;
};
const isPastMonth = (y, m) => {
  const d = new Date();
  return y < d.getFullYear() || (y === d.getFullYear() && m < d.getMonth() + 1);
};

// Convert a saved row (server shape) into the editable draft. Strings make
// controlled inputs easier — we convert back to numbers/null on save.
const toDraft = (row) => ({
  scheduled_due_day: row.scheduled_due_day == null ? '' : String(row.scheduled_due_day),
  scheduled_amount: row.scheduled_amount == null ? '' : String(row.scheduled_amount),
  schedule_note: row.schedule_note || '',
  skipped: !!row.skipped,
});

const sameDraft = (a, b) =>
  a.scheduled_due_day === b.scheduled_due_day
  && a.scheduled_amount === b.scheduled_amount
  && a.schedule_note === b.schedule_note
  && a.skipped === b.skipped;

export function RentScheduleDialog({ open, tenantId, onClose, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  // drafts: keyed by `${year}-${month}` so a row's edits are isolated and
  // we can compute per-row "is dirty" cheaply without touching siblings.
  const [drafts, setDrafts] = useState({});
  const [savingKey, setSavingKey] = useState(null);
  // Bulk apply
  const [bulkDay, setBulkDay] = useState('');
  const [bulkScope, setBulkScope] = useState('future');
  const [bulkBusy, setBulkBusy] = useState(false);

  const reload = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const r = await getTenantRentSchedule(tenantId);
      setData(r);
      const next = {};
      for (const m of r.months) next[`${m.year}-${m.month}`] = toDraft(m);
      setDrafts(next);
      setBulkDay(String(r.default_due_day || ''));
    } catch {
      toast.error('Failed to load schedule');
    } finally { setLoading(false); }
  }, [tenantId]);

  useEffect(() => {
    if (open && tenantId) reload();
    if (!open) { setData(null); setDrafts({}); setBulkDay(''); }
  }, [open, tenantId, reload]);

  const setDraftField = (key, field, value) => {
    setDrafts(prev => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
  };

  const resetRowToServer = (row) => {
    setDrafts(prev => ({ ...prev, [`${row.year}-${row.month}`]: toDraft(row) }));
  };

  // Validate + serialize the draft to the server payload. Throws an
  // error (caught by the caller) if the user typed something invalid.
  const draftToPayload = (draft) => {
    const payload = { schedule_note: draft.schedule_note, skipped: draft.skipped };
    if (draft.scheduled_due_day === '') {
      payload.scheduled_due_day = null;
    } else {
      const n = parseInt(draft.scheduled_due_day, 10);
      if (!n || n < 1 || n > 31) throw new Error('Due day must be 1–31');
      payload.scheduled_due_day = n;
    }
    if (draft.scheduled_amount === '') {
      payload.scheduled_amount = null;
    } else {
      const n = parseFloat(draft.scheduled_amount);
      if (Number.isNaN(n) || n < 0) throw new Error('Amount must be a positive number');
      payload.scheduled_amount = n;
    }
    return payload;
  };

  const saveRow = async (row) => {
    const key = `${row.year}-${row.month}`;
    const draft = drafts[key];
    if (!draft) return;
    let payload;
    try { payload = draftToPayload(draft); }
    catch (e) { toast.error(e.message); return; }

    setSavingKey(key);
    try {
      await updateTenantRentScheduleMonth(tenantId, row.year, row.month, payload);
      // Update the in-memory `data.months` so future renders see the new
      // server-side state without re-fetching.
      setData(prev => {
        if (!prev) return prev;
        return { ...prev, months: prev.months.map(m => {
          if (m.year !== row.year || m.month !== row.month) return m;
          const merged = {
            ...m,
            scheduled_due_day: payload.scheduled_due_day,
            scheduled_amount: payload.scheduled_amount,
            schedule_note: payload.schedule_note,
            skipped: !!payload.skipped,
          };
          merged.effective_due_day = merged.scheduled_due_day || prev.default_due_day;
          merged.effective_amount = merged.scheduled_amount != null
            ? merged.scheduled_amount : prev.monthly_rent;
          return merged;
        }) };
      });
      toast.success(`${MONTH_NAMES[row.month]} ${row.year} saved`);
      onSaved && onSaved();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save');
    } finally { setSavingKey(null); }
  };

  // "Reset to defaults" clears overrides AND removes any skip. Also a
  // distinct quick action so the user doesn't have to manually empty
  // three inputs to get back to the tenant baseline.
  const resetRowOnServer = async (row) => {
    const payload = { scheduled_due_day: null, scheduled_amount: null, schedule_note: '', skipped: false };
    setSavingKey(`${row.year}-${row.month}`);
    try {
      await updateTenantRentScheduleMonth(tenantId, row.year, row.month, payload);
      setData(prev => {
        if (!prev) return prev;
        return { ...prev, months: prev.months.map(m => {
          if (m.year !== row.year || m.month !== row.month) return m;
          return {
            ...m,
            scheduled_due_day: null, scheduled_amount: null,
            schedule_note: '', skipped: false,
            effective_due_day: prev.default_due_day,
            effective_amount: prev.monthly_rent,
          };
        }) };
      });
      setDrafts(prev => ({ ...prev, [`${row.year}-${row.month}`]: {
        scheduled_due_day: '', scheduled_amount: '', schedule_note: '', skipped: false,
      } }));
      toast.success(`${MONTH_NAMES[row.month]} ${row.year} reset`);
      onSaved && onSaved();
    } catch {
      toast.error('Failed to reset');
    } finally { setSavingKey(null); }
  };

  const handleBulkApply = async () => {
    const d = parseInt(bulkDay, 10);
    if (!d || d < 1 || d > 31) { toast.error('Enter a day from 1 to 31'); return; }
    setBulkBusy(true);
    try {
      const r = await bulkUpdateTenantDueDay(tenantId, d, bulkScope);
      toast.success(`Due day set to ${d} for ${r.months_updated} ${r.months_updated === 1 ? 'month' : 'months'}`);
      await reload();
      onSaved && onSaved();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Bulk update failed');
    } finally { setBulkBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-5xl max-h-[88vh] overflow-hidden flex flex-col p-0" data-testid="rent-schedule-dialog">
        <DialogHeader className="px-5 pt-5 pb-3 border-b">
          <DialogTitle className="flex items-center gap-2">
            <CalendarDays className="h-5 w-5" />
            Rent Schedule {data?.tenant_name ? `— ${data.tenant_name}` : ''}
          </DialogTitle>
          <DialogDescription>
            Edit due dates, amounts, and notes per month, or remove a payment so it isn't tracked for that month. Changes here affect this page only — Rent Roll and the tenant record are not touched.
          </DialogDescription>
        </DialogHeader>

        {/* Bulk-apply controls */}
        {data && (
          <div className="px-5 py-3 border-b bg-muted/30 flex flex-wrap items-end gap-3" data-testid="bulk-apply-row">
            <div>
              <Label className="text-xs">Apply due day to…</Label>
              <Input
                type="number" min={1} max={31}
                value={bulkDay}
                onChange={(e) => setBulkDay(e.target.value)}
                className="h-8 w-20 mt-1"
                data-testid="bulk-due-day-input"
              />
            </div>
            <div>
              <Label className="text-xs">Scope</Label>
              <div className="mt-1 inline-flex items-center bg-white rounded-full p-0.5 text-xs border">
                <button
                  type="button"
                  onClick={() => setBulkScope('future')}
                  className={`px-3 py-1 rounded-full transition-colors ${bulkScope === 'future' ? 'bg-primary text-primary-foreground shadow' : 'text-muted-foreground hover:text-foreground'}`}
                  data-testid="bulk-scope-future"
                >Future only</button>
                <button
                  type="button"
                  onClick={() => setBulkScope('all')}
                  className={`px-3 py-1 rounded-full transition-colors ${bulkScope === 'all' ? 'bg-primary text-primary-foreground shadow' : 'text-muted-foreground hover:text-foreground'}`}
                  data-testid="bulk-scope-all"
                >All months</button>
              </div>
            </div>
            <Button size="sm" onClick={handleBulkApply} disabled={bulkBusy} data-testid="bulk-apply-btn">
              {bulkBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
              Apply to {bulkScope === 'future' ? 'future' : 'all'}
            </Button>
            <p className="text-[11px] text-muted-foreground ml-auto self-center">
              Default due day from tenant: <span className="font-medium">{data.default_due_day}</span>
              {data.rent_due_date_raw && data.rent_due_date_raw !== `${data.default_due_day}` && (
                <span className="opacity-60"> ({data.rent_due_date_raw})</span>
              )}
            </p>
          </div>
        )}

        {/* Months table */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading || !data ? (
            <div className="flex items-center justify-center h-40 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white z-10">
                <tr className="text-[11px] uppercase tracking-wider text-muted-foreground border-b">
                  <th className="text-left font-medium px-2 py-2 w-[150px]">Month</th>
                  <th className="text-left font-medium px-2 py-2 w-[110px]">Due day</th>
                  <th className="text-left font-medium px-2 py-2 w-[140px]">Amount</th>
                  <th className="text-left font-medium px-2 py-2">Note</th>
                  <th className="text-right font-medium px-2 py-2 w-[210px]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.months.map((row) => {
                  const key = `${row.year}-${row.month}`;
                  const draft = drafts[key] || toDraft(row);
                  const overridden = row.scheduled_due_day != null || row.scheduled_amount != null || !!row.schedule_note || !!row.skipped;
                  const past = isPastMonth(row.year, row.month);
                  const cur = isCurrentMonth(row.year, row.month);
                  const dirty = !sameDraft(draft, toDraft(row));
                  const busy = savingKey === key;
                  return (
                    <tr
                      key={key}
                      className={`border-b last:border-b-0 ${draft.skipped ? 'opacity-60' : ''} ${cur ? 'bg-primary/5' : past ? 'bg-muted/30' : ''}`}
                      data-testid={`schedule-row-${row.year}-${row.month}`}
                    >
                      <td className="px-2 py-2">
                        <div className="font-medium flex items-center gap-1.5">
                          {MONTH_NAMES[row.month]} {row.year}
                          {row.skipped && (
                            <Badge variant="outline" className="text-[9px] px-1 py-0 text-muted-foreground border-muted-foreground/30">
                              No payment
                            </Badge>
                          )}
                        </div>
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                          {cur ? 'Current' : past ? 'Past' : 'Future'}
                          {row.paid && <span className="ml-1 text-emerald-700">· Paid</span>}
                        </div>
                      </td>
                      <td className="px-2 py-2">
                        <Input
                          type="number" min={1} max={31}
                          value={draft.scheduled_due_day}
                          onChange={(e) => setDraftField(key, 'scheduled_due_day', e.target.value)}
                          disabled={busy || draft.skipped}
                          placeholder={String(data.default_due_day)}
                          className="h-8 w-20"
                          data-testid={`schedule-day-${row.year}-${row.month}`}
                        />
                      </td>
                      <td className="px-2 py-2">
                        <Input
                          type="number" min={0} step="0.01"
                          value={draft.scheduled_amount}
                          onChange={(e) => setDraftField(key, 'scheduled_amount', e.target.value)}
                          disabled={busy || draft.skipped}
                          placeholder={data.monthly_rent ? data.monthly_rent.toLocaleString() : '0.00'}
                          className="h-8 w-32"
                          data-testid={`schedule-amount-${row.year}-${row.month}`}
                        />
                      </td>
                      <td className="px-2 py-2">
                        <Input
                          value={draft.schedule_note}
                          onChange={(e) => setDraftField(key, 'schedule_note', e.target.value)}
                          disabled={busy}
                          placeholder="Optional note for this month…"
                          className="h-8"
                          data-testid={`schedule-note-${row.year}-${row.month}`}
                        />
                      </td>
                      <td className="px-2 py-2">
                        <div className="flex items-center justify-end gap-1">
                          {/* Skip / include toggle */}
                          {draft.skipped ? (
                            <Button
                              size="sm" variant="outline"
                              onClick={() => setDraftField(key, 'skipped', false)}
                              disabled={busy}
                              className="h-7 text-xs gap-1 text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                              data-testid={`schedule-include-${row.year}-${row.month}`}
                            >
                              <CheckCircle2 className="h-3 w-3" /> Add
                            </Button>
                          ) : (
                            <Button
                              size="sm" variant="ghost"
                              onClick={() => setDraftField(key, 'skipped', true)}
                              disabled={busy}
                              className="h-7 text-xs gap-1 text-muted-foreground hover:text-destructive hover:bg-destructive/5"
                              title="Remove this month's payment"
                              data-testid={`schedule-remove-${row.year}-${row.month}`}
                            >
                              <Ban className="h-3 w-3" /> Remove
                            </Button>
                          )}
                          {/* Reset (only if there's a saved override) */}
                          {overridden && (
                            <button
                              type="button"
                              title="Reset this month to tenant defaults"
                              onClick={() => resetRowOnServer(row)}
                              disabled={busy}
                              className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40"
                              data-testid={`schedule-reset-${row.year}-${row.month}`}
                            >
                              <RotateCcw className="h-3.5 w-3.5" />
                            </button>
                          )}
                          {/* Save (only when dirty) */}
                          <Button
                            size="sm"
                            onClick={() => saveRow(row)}
                            disabled={busy || !dirty}
                            className="h-7 text-xs gap-1"
                            data-testid={`schedule-save-${row.year}-${row.month}`}
                          >
                            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                            Save
                          </Button>
                          {dirty && !busy && (
                            <button
                              type="button"
                              onClick={() => resetRowToServer(row)}
                              title="Discard changes"
                              className="p-1.5 rounded text-muted-foreground hover:text-foreground"
                              data-testid={`schedule-discard-${row.year}-${row.month}`}
                            >
                              <RotateCcw className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {data.months.length === 0 && (
                  <tr><td colSpan={5} className="text-center py-6 text-muted-foreground">No months in this tenant's lease range.</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        <div className="px-5 py-3 border-t flex items-center justify-between bg-muted/20">
          <p className="text-[11px] text-muted-foreground">
            Default rent: <span className="font-medium">{fmtMoney(data?.monthly_rent)}</span>
            {data && (() => {
              const active = data.months.filter(m => !m.skipped);
              const total = active.reduce((s, m) => s + (m.effective_amount || 0), 0);
              return (
                <span className="ml-3">
                  Scheduled total: <span className="font-medium">{fmtMoney(total)}</span>
                  <span className="opacity-60"> · {active.length} {active.length === 1 ? 'month' : 'months'}</span>
                </span>
              );
            })()}
          </p>
          <Button variant="outline" size="sm" onClick={onClose} data-testid="schedule-close-btn">Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
